import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '../Header/DashboardHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashAlt, faSearch, faEye } from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ViewBlogs.css';

function ViewBlogs() {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if admin is logged in
  useEffect(() => {
    const isLoggedIn = sessionStorage.getItem('admin-login');
    if (!isLoggedIn) {
      // Redirect to home page if not logged in
      navigate('/');
    } else {
      setIsAuthorized(true);
    }
  }, [navigate]);

  // Fetch blogs data from the backend API
  const fetchBlogs = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('https://b.centrexmarineservices.com/Api/View.php');

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === 'success') {
        setBlogs(data.data || []);
        setFilteredBlogs(data.data || []);
        setError(null);
      } else {
        setError(data.message || 'Failed to fetch blogs');
        toast.error(data.message || 'Failed to fetch blogs');
        setBlogs([]);
        setFilteredBlogs([]);
      }
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError('Failed to fetch blogs. Please try again later.');
      toast.error('Failed to fetch blogs. Please try again later.');
      setBlogs([]);
      setFilteredBlogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthorized) {
      fetchBlogs();
    }
  }, [isAuthorized]);

  // Filter blogs based on search term
  useEffect(() => {
    const results = blogs.filter(blog =>
      blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (blog.excerpt && blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (blog.content && blog.content.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredBlogs(results);
  }, [searchTerm, blogs]);

  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = (id) => {
    setConfirmDelete(id);
  };

  // Handle actual deletion
  const handleDelete = async (id) => {
    // Prevent multiple delete requests
    if (isDeleting) return;
    
    try {
      setIsDeleting(true);
      
      const response = await fetch('https://b.centrexmarineservices.com/Api/Delete.php', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: id })
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        // Remove the deleted blog from the state
        setBlogs(blogs.filter(blog => blog.id !== id));
        // Show success toast
        toast.success('Blog deleted successfully!');
      } else {
        // Show error toast
        toast.error(data.message || 'Failed to delete blog. Please try again.');
      }
    } catch (err) {
      console.error('Error deleting blog:', err);
      toast.error('An error occurred while deleting the blog. Please try again later.');
    } finally {
      setConfirmDelete(null);
      setIsDeleting(false);
    }
  };

  // Handle edit navigation
  const handleEdit = (id) => {
    // Navigate to edit page with the blog id
    navigate(`/dashboard/update-blog/${id}`);
  };

  // Handle view navigation
  const handleView = (id) => {
    // Navigate to the public blog page
    navigate(`/blog/${id}`);
  };

  // Create excerpt from content if excerpt is empty
  const getExcerpt = (blog) => {
    if (blog.excerpt && blog.excerpt.trim() !== '') {
      return blog.excerpt;
    }

    // If no excerpt, create one from content (first 100 characters)
    if (blog.content) {
      return blog.content.substring(0, 100) + '...';
    }

    return 'No content available';
  };

  // Only render content if user is authorized
  if (!isAuthorized) {
    return null; // Return nothing while checking authorization or redirecting
  }

  return (
    <div className="view-blogs-page">
      <DashboardHeader />

      <div className="view-blogs-container">
        <div className="view-blogs-header">
          <h1>Manage Blog Posts</h1>
        </div>

        <div className="blogs-management-panel">
          <div className="search-filter-container">
            <div className="search-container">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                placeholder="Search blogs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="blogs-stats">
              <div className="stat-item">
                <span className="stat-value">{blogs.length}</span>
                <span className="stat-label">Total Blogs</span>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading blogs...</p>
            </div>
          ) : error ? (
            <div className="error-message">
              <p>{error}</p>
            </div>
          ) : filteredBlogs.length === 0 ? (
            <div className="no-blogs-message">
              <p>No blogs found matching your search criteria.</p>
            </div>
          ) : (
            <div className="blogs-table-container">
              <table className="blogs-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Title</th>
                    <th>Content</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBlogs.map(blog => (
                    <tr key={blog.id} className={confirmDelete === blog.id ? 'delete-confirm' : ''}>
                      <td className="blog-image-cell">
                        {blog.image_path ? (
                          <img
                            src={blog.image_path}
                            alt={blog.title}
                            className="blog-thumbnail"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/placeholder-image.jpg'; // Replace with a default placeholder image
                              console.log(`Failed to load image: ${blog.image_path}`);
                            }}
                          />
                        ) : (
                          <div className="no-image">No Image</div>
                        )}
                      </td>

                      <td className="blog-title-cell">{blog.title}</td>
                      <td className="blog-excerpt-cell">{getExcerpt(blog)}</td>
                      <td className="blog-date-cell">{formatDate(blog.date_published)}</td>
                      <td className="blog-actions-cell">
                        {confirmDelete === blog.id ? (
                          <div className="delete-confirmation">
                            <p>Are you sure?</p>
                            <div className="confirmation-buttons">
                              <button
                                className="confirm-yes"
                                onClick={() => handleDelete(blog.id)}
                                disabled={isDeleting}
                              >
                                {isDeleting ? 'Deleting...' : 'Yes'}
                              </button>
                              <button
                                className="confirm-no"
                                onClick={() => setConfirmDelete(null)}
                                disabled={isDeleting}
                              >
                                No
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="action-buttons">
                            <button
                              className="view-btn"
                              onClick={() => handleView(blog.id)}
                              title="View Blog"
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                            <button
                              className="edit-btn"
                              onClick={() => handleEdit(blog.id)}
                              title="Edit Blog"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              className="delete-btn"
                              onClick={() => handleDeleteConfirm(blog.id)}
                              title="Delete Blog"
                            >
                              <FontAwesomeIcon icon={faTrashAlt} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* Toast container for notifications */}
      <ToastContainer 
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}

export default ViewBlogs;
